import React, { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import StripeInstance from "common/StripeInstance";
import { stripeAddBankAccount, stripeAddExternalBankAccount } from "services/api/haulpay";
import Button from "components/Button";
import Icon from "@mdi/react";
import { mdiLoading } from "@mdi/js";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import ConfirmationModal from "components/ConfirmationModal";
import { useSelector } from "react-redux";
import { selectUserDetails } from "store/user/selector";
import { useToggle } from "usehooks-ts";
import ConfirmMicroDepositDepositModel from "./ConfirmMicroDepositDepositModel";

interface Props {
  accountId: string;
  isBroker?: boolean;
  closeHandler: () => void;
}

const AddStripeBankAccount: React.FC<Props> = ({ accountId, isBroker = false, closeHandler }) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isStripeConfirmationBankModalOpen, setIsStripeConfirmationBankModalOpen] =
    useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [microDepositOpenModel, setMicroDepositOpenModel] = useToggle(false);
  const [stripeAccount, setStripeAccount] = useState<any>(null);
  const userDetails = useSelector(selectUserDetails);
  const queryClient = useQueryClient();

  const addBankAccountHandler = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setIsProcessing(true);
      try {
        const res = await stripeAddBankAccount(userDetails.factoring_id);
        setStripeAccount(res?.data);
        const params = {
          payment_method_type: "us_bank_account",
          payment_method_data: {
            billing_details: {
              name: res?.data?.owner_name || res?.data?.company_name,
              email: res?.data?.email
            }
          }
        };
        const options = {
          clientSecret: res?.data?.client_secret,
          params,
          expand: ["payment_method"]
        };
        const data = await StripeInstance.getInstance(
          res?.data?.stripe_account_id
        ).collectBankAccountForSetup(options);
        if (data?.error) {
          setIsProcessing(false);
          return showErrorMessage(data?.error?.message || "An error occurred");
        }

        if (data?.setupIntent?.status === "requires_confirmation") {
          setIsStripeConfirmationBankModalOpen(true);
          setData(data);
        }
      } catch (error: any) {
        return showErrorMessage(error?.message || "An error occurred");
      } finally {
        setIsProcessing(false);
      }
    },
    [userDetails.factoring_id, accountId]
  );

  const bankAccountConfirmationHandler = useCallback(async () => {
    try {
      StripeInstance.getInstance(stripeAccount?.stripe_account_id)
        .confirmUsBankAccountSetup(data?.setupIntent.client_secret, {
          payment_method: data?.setupIntent.payment_method?.id
        })
        .then(async (response) => {
          if (response?.error) {
            setIsProcessing(false);
            return showErrorMessage(response?.error.message || "An error occurred");
          } else if (response?.setupIntent?.status === "succeeded") {
            showSuccessMessage("Bank account successfully added.");
            await stripeAddExternalBankAccount(data?.setupIntent?.payment_method?.id, accountId);
            await queryClient.invalidateQueries(["fetch_stripe_account"]);
          } else if (data?.setupIntent?.next_action?.type === "verify_with_microdeposits") {
            // Handle microdeposit verification steps here.
          }
        });
    } catch (error: any) {
      return showErrorMessage(error?.message || "An error occurred");
    } finally {
      setIsStripeConfirmationBankModalOpen(false);
      closeHandler();
    }
  }, [data, accountId, stripeAccount]);

  return (
    <>
      <Button
        onClick={addBankAccountHandler}
        disabled={isProcessing}
        className={`px-4 py-2 rounded text-white font-semibold transition-colors duration-200 ${
          isProcessing ? "bg-gray-400" : "bg-green-500 hover:bg-green-600"
        }`}>
        Add Bank Account
        {isProcessing && <Icon path={mdiLoading} size={1} spin={1} />}
      </Button>

      <div className="my-4 text-gray-500">
        <span>OR</span>
      </div>

      {isBroker && (
        <button
          onClick={setMicroDepositOpenModel}
          disabled={isProcessing}
          className={`px-4 py-2 rounded text-white font-semibold transition-colors duration-200 ${
            isProcessing ? "bg-gray-400" : "bg-orange-500 hover:bg-orange-600"
          }`}>
          Received Micro Deposit
        </button>
      )}
      <ConfirmationModal
        content={
          `By clicking “Confirm”, you authorize HaulPay to debit the bank account specified above for any amount owed for charges arising from your use of HaulPay’s services and/or purchase of products from HaulPay, pursuant to HaulPay’s website and terms, until this authorization is revoked. You may amend or cancel this authorization at any time by providing notice to HaulPay with 30 (thirty) days notice.` +
          `\n\n If you use HaulPay’s services or purchase additional products periodically pursuant to HaulPay’s terms, you authorize HaulPay to debit your bank account periodically. Payments that fall outside of the regular debits authorized above will only be debited after your authorization is obtained.`
        }
        isConfirmModalOpen={isStripeConfirmationBankModalOpen}
        toggleConfirmModal={() => setIsStripeConfirmationBankModalOpen(false)}
        onOK={bankAccountConfirmationHandler}
        labelOKButton="Confirm"
        isHideCancelButton={true}
      />
      <ConfirmMicroDepositDepositModel
        open={microDepositOpenModel}
        closeHandler={setMicroDepositOpenModel}
      />
    </>
  );
};

export default AddStripeBankAccount;
